import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { LuDownload, LuX } from 'react-icons/lu';

import { DocumentDownloadRow } from './DocumentDownloadRow';
import Spinner from '~/components/icons/Spinner';
import { useChatMediaUrl } from '~/features/whatsapp/hooks/useChatMediaUrl';

type Props = {
    chatId: string;
    displayName: string;
    downloadName: string;
    mediaKey: string;
    name?: string;
};

export const ImageAttachment = ({ chatId, displayName, downloadName, mediaKey, name }: Props) => {
    const { t } = useTranslation();
    const {
        blobUrl,
        downloadFileName: downloadFileNameFromApi,
        isError,
        isLoading,
    } = useChatMediaUrl(chatId, mediaKey);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const resolvedDownloadName = downloadFileNameFromApi ?? downloadName;

    if (isLoading || (!blobUrl && !isError)) {
        return (
            <div className="flex h-24 w-40 items-center justify-center rounded-md bg-grey-light">
                <Spinner className="h-5 w-5" />
            </div>
        );
    }

    if (isError || !blobUrl) {
        return <DocumentDownloadRow chatId={chatId} displayName={displayName} mediaKey={mediaKey} />;
    }

    return (
        <>
            <div className="relative inline-block max-w-60">
                <button type="button" onClick={() => setLightboxOpen(true)} className="block w-full">
                    <img src={blobUrl} alt={name ?? ''} className="max-h-60 rounded-md object-cover" />
                </button>
                <a
                    href={blobUrl}
                    download={resolvedDownloadName}
                    onClick={(e) => e.stopPropagation()}
                    className="absolute bottom-1.5 right-1.5 rounded-full bg-black/50 p-1.5 text-white hover:bg-black/70"
                    title={t('WHATSAPP_CHAT.ATTACHMENT.DOWNLOAD')}
                    aria-label={t('WHATSAPP_CHAT.ATTACHMENT.DOWNLOAD')}
                >
                    <LuDownload size={14} />
                </a>
            </div>
            {lightboxOpen && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80"
                    onClick={() => setLightboxOpen(false)}
                    onKeyDown={(e) => e.key === 'Escape' && setLightboxOpen(false)}
                    role="presentation"
                >
                    <div className="absolute right-4 top-4 flex items-center gap-3 text-white">
                        <a
                            href={blobUrl}
                            download={resolvedDownloadName}
                            onClick={(e) => e.stopPropagation()}
                            title={t('WHATSAPP_CHAT.ATTACHMENT.DOWNLOAD')}
                            aria-label={t('WHATSAPP_CHAT.ATTACHMENT.DOWNLOAD')}
                        >
                            <LuDownload size={24} />
                        </a>
                        <button type="button" onClick={() => setLightboxOpen(false)}>
                            <LuX size={24} />
                        </button>
                    </div>
                    <img
                        src={blobUrl}
                        alt={name ?? ''}
                        className="max-h-[90vh] max-w-[90vw] object-contain"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}
        </>
    );
};
